import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { TextField } from '@material-ui/core';

//iconos
import { Weekend, Timeline, History, PhotoCamera, Store, Compare } from '@material-ui/icons';

//tabla y card components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardIcon from 'components/Card/CardIcon.js';
import CardHeader from 'components/Card/CardHeader.js';
import ReactTable from 'components/ReactTable/ReactTableV2.js';
import { cardTitle } from 'assets/jss/material-dashboard-pro-react.js';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';

//servicios
import { wsGet, wsFindBy } from 'services/WService.js';

import { nombreEstado } from './estados';
import ButtonDelete from 'hooks/ButtonDelete';
import { AllowedGuard } from 'components/Tvendo/Common/UserAction';
import { ConstantPermissions } from 'contexts/useConstant';
import { useModal } from 'hooks/useModal';
import CompararFotosModal from './CompararFotosModal';
import { Toasty, useToasty } from 'hooks/Toasty';
import { WithApp } from 'contexts/appContext';

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px'
  },
  dato: {
    marginBottom: '8px'
  }
};
const useStyles = makeStyles(styles);

const columnasVentas = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 80 },
  { Header: 'Pedido ID', accessor: 'id', width: 250 },
  { Header: 'Fecha', accessor: 'date', width: 300 },
  { Header: 'Unidades', accessor: 'unidades', width: 200 },
  { Header: 'Monto', accessor: 'monto' },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

const columnasHistorico = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 80 },
  { Header: 'Periodo', accessor: 'period', width: 150 },
  { Header: 'Venta', accessor: 'sales', width: 170 },
  { Header: 'Categoría', accessor: 'category' },
  { Header: 'Calculado', accessor: 'calculated', width: 180 },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

// Las visitas al mueble. Van en su propia tabla y no dentro de las ventas: la
// foto es del día, no del pedido, y es una sola por vendedor y día.
const columnasVisitas = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 120 },
  { Header: 'Fecha', accessor: 'date' },
  { Header: 'Cómo lo encontró', accessor: 'fotoAntes', excelEnabled: false, width: 240 },
  { Header: 'Cómo lo dejó', accessor: 'fotoDespues', excelEnabled: false, width: 130 },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

//las fechas del modulo son columnas date: la hora siempre viene en 00:00
const fecha = (v) => (v ? moment(v).format('DD-MM-YYYY') : '');

//calculated_at si es un timestamp real, ahi la hora dice cuando corrio el calculo
const fechaHora = (v) => (v ? moment(v).format('DD-MM-YYYY HH:mm') : '');

//quantity llega como decimal de SQL Server (1.000000) y son unidades enteras
const entero = (v) => (v === null || v === undefined || v === '' ? '' : parseInt(v, 10));

const moneda = (v) =>
  v === null || v === undefined
    ? ''
    : Number(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Detalle de un control de mueble.
 *
 * Tres bloques: los datos del control con su foto, las ventas del cliente en
 * un rango, y el histórico de categorías.
 *
 * El histórico sale de base_customer_furniture_logs y es lo único que muestra
 * cómo se movió el cliente mes a mes: no se puede reconstruir desde las ventas
 * porque los tramos de las categorías pueden haber cambiado desde entonces.
 */
export default WithApp(function CustomerFurnitureDetail(props) {
  const { id } = useParams();
  const classes = useStyles();

  const [notify, showNotify] = useToasty();
  const [control, setControl] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  const [ventas, setVentas] = useState([]);
  const [visitas, setVisitas] = useState([]);
  const [total, setTotal] = useState(0);
  const [rango, setRango] = useState({ from: '', to: '' });

  //la visita que se esta mirando en grande
  const [visitaFotos, setVerFotos] = useModal();

  //miniatura clickeable, o vacío si esa foto no se cargó
  const miniatura = (url, alt) =>
    url ? (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={alt}
          style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 4 }}
        />
      </a>
    ) : (
      ''
    );

  async function loadControl() {
    setDataLoading(true);
    let response = await wsGet('base/customer-furniture', id);
    setDataLoading(false);
    if (response.success) setControl(response.data);
    else showNotify(response.message, 'error');
  }

  async function loadVentas(filtros = {}) {
    setDataLoading(true);
    let response = await wsFindBy(`base/customer-furniture/${id}/sales`, filtros);
    setDataLoading(false);

    if (response.success) {
      setVentas(response.data || []);
      setVisitas(response.visits || []);
      setTotal(response.total || 0);
      //el backend informa qué rango usó cuando no se le manda ninguno
      if (!filtros.from && response.from) setRango({ from: response.from, to: response.to });
    }
  }

  useEffect(() => {
    loadControl();
    loadVentas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <GridContainer>
      <Toasty action={notify} />

      {visitaFotos.open ? (
        <CompararFotosModal model={visitaFotos} setOpen={setVerFotos} />
      ) : undefined}

      {/* Datos del control */}
      <GridItem xs={12} md={7}>
        <Card>
          <CardHeader icon>
            <CardIcon color="primary">
              <Weekend style={{ color: 'white' }} />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Control de mueble {dataLoading && <LoadingSpinner />}
            </h4>
          </CardHeader>
          <CardBody>
            <div className={classes.dato}>
              <strong>Cliente:</strong> {control?.customer?.CardCode} - {control?.customer?.CardName}
            </div>
            <div className={classes.dato}>
              <strong>RUC / DNI:</strong> {control?.customer?.LicTradNum || '—'}
            </div>
            <div className={classes.dato}>
              <strong>Dirección:</strong> {control?.address?.Address2 || '—'}
            </div>
            <div className={classes.dato}>
              <strong>Mueble:</strong>{' '}
              {control?.furniture
                ? `${control.furniture.code ? control.furniture.code + ' - ' : ''}${control.furniture.description}`
                : '—'}
            </div>
            <div className={classes.dato}>
              <strong>N° de serie:</strong> {control?.furniture?.serial_number || '—'}
            </div>
            <div className={classes.dato}>
              <strong>Proveedor:</strong> {control?.furniture?.provider?.CardName || '—'}
            </div>
            <div className={classes.dato}>
              <strong>Estado:</strong> {nombreEstado(control?.state) || '—'}
            </div>
            <div className={classes.dato}>
              <strong>Fecha de alta:</strong>{' '}
              {String(control?.created_at || '').slice(0, 10) || '—'}
            </div>
            <div className={classes.dato}>
              <strong>Fecha de baja:</strong> {control?.unregistered_at || 'Sigue colocado'}
            </div>
            <div className={classes.dato}>
              <strong>Venta Arcor acumulada:</strong>{' '}
              {control?.sales_amount === null || control?.sales_amount === undefined
                ? 'Sin calcular'
                : `S/ ${moneda(control.sales_amount)}`}
            </div>
            <div className={classes.dato}>
              <strong>Rango venta:</strong>{' '}
              {control?.sales_category?.description || 'Sin categoría'}
            </div>
            <div className={classes.dato}>
              <strong>Último cálculo:</strong>{' '}
              {control?.calculated_at
                ? fechaHora(control.calculated_at)
                : 'Nunca se calculó'}
            </div>
          </CardBody>
        </Card>
      </GridItem>

      {/* Foto */}
      <GridItem xs={12} md={5}>
        <Card>
          <CardHeader icon>
            <CardIcon color={props.themeContext.buttonColorWarning}>
              <PhotoCamera style={{ color: 'white' }} />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Foto del mueble</h4>
          </CardHeader>
          <CardBody>
            {control?.furniture?.file_url ? (
              <img
                src={control.furniture.file_url}
                alt={control?.furniture?.description || 'Mueble'}
                style={{ width: '100%', borderRadius: 6 }}
              />
            ) : (
              <p>El mueble todavía no tiene foto cargada en el catálogo.</p>
            )}
          </CardBody>
        </Card>
      </GridItem>

      {/* Ventas del cliente en el rango */}
      <GridItem xs={12}>
        <Card>
          <CardHeader icon>
            <CardIcon color="primary">
              <Timeline style={{ color: 'white' }} />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Ventas · Total del periodo S/ {moneda(total)} · {ventas.length} pedido(s)
              {dataLoading && <LoadingSpinner />}
            </h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12} sm={3}>
                <TextField
                  label="Desde"
                  type="date"
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={rango.from}
                  onChange={(e) => setRango({ ...rango, from: e.target.value })}
                />
              </GridItem>
              <GridItem xs={12} sm={3}>
                <TextField
                  label="Hasta"
                  type="date"
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={rango.to}
                  onChange={(e) => setRango({ ...rango, to: e.target.value })}
                />
              </GridItem>
              <GridItem xs={12} sm={3}>
                <Button
                  color="info"
                  size="sm"
                  style={{ marginTop: '25px' }}
                  disabled={dataLoading}
                  onClick={() => loadVentas(rango)}
                >
                  Consultar
                </Button>
              </GridItem>
            </GridContainer>

            <ReactTable
              excelName="Ventas del cliente"
              defaultWidth={1200}
              columns={columnasVentas}
              data={ventas.map((item, i) => {
                return {
                  num: (
                    <div className="actions-right">
                      {i + 1}
                    </div>
                  ),
                  id: item.id ? item.id.toString() : '',
                  date: fecha(item.date_order),
                  unidades: entero(item.unidades),
                  monto: moneda(item.monto),
                  actions: '',
                };
              })}
            />
          </CardBody>
        </Card>
      </GridItem>

      {/* Visitas al mueble. Solo aparece si las hay: sin fotos cargadas la
          tarjeta no tiene por qué ocupar lugar. */}
      {visitas.length > 0 && (
        <GridItem xs={12}>
          <Card>
            <CardHeader icon>
              <CardIcon color={props.themeContext.buttonColorWarning}>
                <Store style={{ color: 'white' }} />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                Visitas al mueble ({visitas.length})
              </h4>
            </CardHeader>
            <CardBody>
              <ReactTable
                excelName="Visitas al mueble"
                defaultWidth={1200}
                columns={columnasVisitas}
                data={visitas.map((v, i) => {
                  return {
                    num: (
                      <div className="actions-right">
                        {i + 1}
                        <Button
                          justIcon
                          round
                          simple
                          color={props.themeContext.buttonColorInfo}
                          title="Comparar las dos fotos"
                          onClick={() => setVerFotos(v)}
                        >
                          <Compare />
                        </Button>
                        <AllowedGuard permission={ConstantPermissions.BASE_FURNITURES_DESTROY}>
                          <ButtonDelete
                            entity="base/customer-furniture/visit"
                            id={v.id}
                            reload={() => loadVentas(rango)}
                          />
                        </AllowedGuard>
                      </div>
                    ),
                    date: fecha(v.visit_date),
                    fotoAntes: miniatura(v.found_file_url, 'Cómo lo encontró'),
                    fotoDespues: miniatura(v.left_file_url, 'Cómo lo dejó'),
                    actions: '',
                  };
                })}
              />
            </CardBody>
          </Card>
        </GridItem>
      )}

      {/* Histórico de categorías */}
      <GridItem xs={12}>
        <Card>
          <CardHeader icon>
            <CardIcon color={props.themeContext.buttonColorSuccess}>
              <History style={{ color: 'white' }} />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Histórico de categorías</h4>
          </CardHeader>
          <CardBody>
            <ReactTable
              excelName="Historico de categorias"
              defaultWidth={1200}
              columns={columnasHistorico}
              data={(control?.logs || []).map((l, i) => {
                return {
                  num: (
                    <div className="actions-right">
                      {i + 1}
                    </div>
                  ),
                  period: l.period,
                  sales: moneda(l.sales_amount),
                  category: l.sales_category?.description || 'Sin categoría',
                  calculated: fechaHora(l.calculated_at),
                  actions: '',
                };
              })}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
});
